import {DialogData, InputDialogComponent} from '@widgets/input-dialog/input-dialog.component';
import {Location, CommonModule} from '@angular/common';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '@widgets/confirm-dialog/confirm-dialog.component';
import {ProductViewComponent} from '@app/product-view/product-view.component';
import {QueryAndIndex, SearchproductService} from '@services/searchproduct.service';
import {Product} from '@services/model/product';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {ApplicationService} from '@services/application.service';
import {UUID} from '@util/types';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {repeatIf} from '@util/rxjs';
import {Application} from '@services/model/application';
import {none, Option} from 'fp-ts/lib/Option';
import {ProductService} from '@services/product.service';
import {ResetApplicationComponent, ResetApplicationComponentData} from '@ma-dashboard/reset-application/reset-application.component';
import {StartupService} from '@services/startup.service';
import {Cycle} from '@services/model/cycle';
import moment from 'moment';
import {VoilaUserService} from '@services/voila-user.service';
import {MarkantElementsModule} from '@markant/elements-angular';
import {ProductNavComponent} from '@app/product-view/product-nav/product-nav.component';
import {OrderSampleButtonComponent} from '@app/product-view/order-sample-button/order-sample-button.component';
import {MinPrivilegeDirective} from '@util/directives/min-privilege.directive';
import {OptionPipe} from '@util/pipes/option-pipe';
import {ProductStateTranslate} from '@util/pipes/product-state-translate';
import {SingletonPipe} from '@util/pipes/singleton-pipe';
import {RouteBuilder} from '@util/route-builder';

@Component({
  selector: 'app-ma-product-view',
  templateUrl: './ma-product-view.component.html',
  styleUrls: ['./ma-product-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    ProductNavComponent,
    RouterLink,
    ProductViewComponent,
    OrderSampleButtonComponent,
    MinPrivilegeDirective,
    OptionPipe,
    ProductStateTranslate,
    SingletonPipe,
  ],
})
export class MaProductViewComponent implements OnInit {
  productId$: Observable<UUID>;
  queryAndIndex$: Observable<QueryAndIndex>;

  private readonly reloadProduct$: Subject<void> = new Subject<void>();
  reloadApplication: Subject<void> = new Subject();

  application$: Observable<Option<Application>>;
  product$: Observable<Product>;
  favoriteProduct$: Observable<boolean>;

  @ViewChild(ProductViewComponent, {static: true})
  productViewComponent: ProductViewComponent;

  constructor(
    readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly startupService: StartupService,
    private readonly productService: ProductService,
    private readonly applicationService: ApplicationService,
    readonly userService: VoilaUserService,
    public readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.productId$ = this.route.paramMap.pipe(
      repeatIf(this.reloadProduct$),
      map((pm) => pm.get('productId') as string),
    );

    this.product$ = this.productId$.pipe(
      repeatIf(this.reloadProduct$),
      switchMap((pid) => this.productService.getProduct(pid)),
      shareReplay(1),
    );

    this.queryAndIndex$ = this.route.queryParamMap.pipe(SearchproductService.extractQueryAndIndex());

    /* load application if relevant */
    this.application$ = combineLatest([this.productId$, this.queryAndIndex$]).pipe(
      repeatIf(this.reloadApplication),
      switchMap((x) => {
        const productId = x[0];
        const cf = x[1].query.cycleFilter;
        if (cf && cf !== 'none' && cf !== 'all') {
          return this.applicationService.readApplication(productId, cf);
        } else {
          return of(none);
        }
      }),
    );
    this.getFavoriteProductStatus();
  }

  approve(productId: UUID, cycleId: UUID): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {title: 'Bestätigen', message: 'Soll dieses Produkt wirklich bestätigt werden ?'},
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.applicationService.approveProduct(productId, cycleId).subscribe(() => this.reloadApplication.next());
      }
    });
  }

  reject(productId: UUID, cycleId: UUID): void {
    const dialogData: DialogData = {
      title: 'Ablehnen',
      message:
        'Hier kann die Bewerbung eines Start-ups abgelehnt werden. Das Start-up wird eine E-Mail erhalten. Schreiben Sie hier einen Kommentar um dem Start-up eine Information zugeben, warum die Bewerbung abgelehnt wurde.',
      inputLabel: 'Ihre Nachricht an das Start-up',
    };
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = dialogData;
    dialogConfig.width = '600px';

    const dialogRef = this.dialog.open(InputDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (dialogResult.confirmed === true) {
        this.applicationService.rejectProduct(productId, cycleId, dialogResult.input).subscribe((a) => this.reloadApplication.next());
      }
    });
  }

  resetRequest(productId: UUID, cycleId: UUID): void {
    const data: ResetApplicationComponentData = {productId, cycleId};
    const dialogRef = this.dialog.open(ResetApplicationComponent, {data, width: '600px'});
    dialogRef.afterClosed().subscribe((result) => {
      // TODO: There's a race condition sleeping here
      this.reloadApplication.next();
    });
  }

  setFavoriteProduct(productId: UUID) {
    this.startupService.setFavoriteProduct(this.userService.currentUser().id, productId).subscribe((result) => {
      this.getFavoriteProductStatus();
    });
  }

  deleteFavoriteProduct(productId: UUID) {
    this.startupService.deleteFavoriteProduct(this.userService.currentUser().id, productId).subscribe((result) => {
      this.getFavoriteProductStatus();
    });
  }

  approveOrResetApplicationPossible(app: Application): boolean {
    return app.productStatus === 'requested' || (app.productStatus === 'approved' && this.inApplicationPeriod(app.cycle));
  }

  approveApplicationPossible(app: Application): boolean {
    return app.productStatus === 'requested';
  }

  inApplicationPeriod(c: Cycle): boolean {
    const now = moment();
    return now.isBetween(c.applicationStart, c.applicationEnd);
  }

  protected getProductEditRoute(productId: UUID): string {
    return RouteBuilder.app().maDashboard().productEdit(productId).build();
  }

  private getFavoriteProductStatus() {
    this.productId$.subscribe((id) => {
      this.favoriteProduct$ = this.startupService.getFavoriteProductStatus(this.userService.currentUser().id, id);
    });
  }
  protected readonly RouteBuilder = RouteBuilder;
}
